import classNames from 'classnames';
import { useRef } from 'react';
import { Image } from 'react-feather';
import transformImageUrl from '../../../utils/image-transformation';
import { MAX_REASONABLE_SIZE } from '../../business/profile-header';
import Spinner from '../../spinner';
import { ImageUpload } from './helpers';

interface Props {
  /**
   * Short name for image. Will show up as "Upload ${label}" or "Change ${label}"
   */
  label?: string;
  /**
   * Classes to apply to root element
   */
  className?: string;
  layout?: 'horizontal' | 'vertical';
  imageUpload: ImageUpload;
  required?: boolean;
  profilePicture?: boolean;
}

const ImageInput: React.FC<Props> = ({
  imageUpload,
  label = 'image',
  layout = 'vertical',
  className,
  required,
  profilePicture
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <div
        className={classNames(
          className,
          'image-input rounded flex relative justify-center items-center bg-contain bg-no-repeat bg-center',
          layout === 'vertical' ? 'flex-col flex-wrap' : 'flex-row'
        )}
        style={{
          backgroundImage: imageUpload.imageUrl
            ? `url(${
                profilePicture
                  ? transformImageUrl(imageUpload.imageUrl, {
                      width: MAX_REASONABLE_SIZE,
                      height: MAX_REASONABLE_SIZE
                    })
                  : imageUpload.imageUrl
              })`
            : 'none'
        }}
      >
        <input
          type='file'
          className='file-input'
          style={{ fontSize: '0' }}
          accept='image/*'
          tabIndex={-1}
          ref={fileInputRef}
          onChange={imageUpload.uploadFromInput}
          required={required}
        />
        <div className='p-2'>{imageUpload.uploading ? <Spinner /> : <Image size='24' />}</div>
        <div className='p-2 text-sm leading-tight'>
          <button
            className='tertiary text-left'
            type='button'
            onClick={() => (imageUpload.imageUrl ? imageUpload.removeImage() : fileInputRef.current?.click?.())}
          >
            {imageUpload.imageUrl ? `Reset` : `Upload ${label}`}
          </button>
          {imageUpload.error && <div className='text-red-700'>Upload failed</div>}
        </div>
      </div>
      <style jsx>{`
        .image-input {
          max-width: 16rem;
          background-color: var(--input-background);
        }
        .file-input {
          position: absolute;
          z-index: -1;
          bottom: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 1px !important;
          min-width: 1px !important;
          height: 1px !important;
          min-height: 1px !important;
        }
        .uploading {
          perspective: 100px;
          animation: uploading 4s linear infinite;
        }
        button.tertiary {
          text-shadow: 0 1px 4px rgba(0, 0, 0, 0.9);
        }
        @keyframes uploading {
          0% {
            transform: none;
          }
          25% {
            transform: rotateX(180deg);
          }
          50% {
            transform: rotateX(180deg) rotateY(180deg);
          }
          75% {
            transform: rotateY(180deg);
          }
          100% {
            transform: none;
          }
        }
      `}</style>
    </>
  );
};

export default ImageInput;
